import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaCcStripe } from 'react-icons/fa';
import { branding } from '../constants/branding';
import axios from 'axios';
import axiosInstance from '../services/axiosConfig';
import { ProductSelection } from '../components/common/ProductSelection';
import './PersonalRegister.css';
import { RootState } from '../store';
import { updatePersonalForm, clearPersonalForm } from '../store/slices/registrationSlice';
import { PersonalFormData } from '../store/types/registration.types';
import SumsubKYC from '../components/kyc/SumsubKYC';
import { kycService, type KYCApplicantData } from '../services/kycService';
import { mockKYCService } from '../services/mockKYCService';
import { getApiBaseUrl } from '../utils/apiConfig';
import { emailVerificationService } from '../services/emailVerificationService';

// Initialize CSS variables from branding
const initializeBrandingStyles = () => {
  const root = document.documentElement;
  
  // Sidebar
  root.style.setProperty('--sidebar-width', branding.sidebar.width);
  root.style.setProperty('--sidebar-background', branding.sidebar.background);
  root.style.setProperty('--sidebar-padding', branding.sidebar.padding);
  root.style.setProperty('--sidebar-text-color', branding.sidebar.textColor);
  
  // Registration Main
  root.style.setProperty('--registration-main-padding', branding.registration.main.padding);
  root.style.setProperty('--registration-main-background', branding.registration.main.background);
  root.style.setProperty('--registration-main-max-width', branding.registration.main.maxWidth);
  
  // Stepper
  root.style.setProperty('--registration-stepper-width', branding.registration.stepper.width);
  root.style.setProperty('--registration-stepper-number-size', branding.registration.stepper.numberSize);
  root.style.setProperty('--registration-stepper-font-size', branding.registration.stepper.fontSize);
  root.style.setProperty('--registration-stepper-line-height', branding.registration.stepper.lineHeight);
  root.style.setProperty('--registration-stepper-active-color', branding.registration.stepper.activeColor);
  root.style.setProperty('--registration-stepper-completed-color', branding.registration.stepper.completedColor);
  root.style.setProperty('--registration-stepper-inactive-color', branding.registration.stepper.inactiveColor);
  root.style.setProperty('--registration-stepper-line-color', branding.registration.stepper.lineColor);
  root.style.setProperty('--registration-stepper-line-thickness', branding.registration.stepper.lineThickness);
  
  // Content
  root.style.setProperty('--registration-content-max-width', branding.registration.content.maxWidth);
  
  // Form Elements
  root.style.setProperty('--form-label-font-size', `${branding.forms.label.fontSize}px`);
  root.style.setProperty('--form-label-font-weight', branding.forms.label.fontWeight.toString());
  root.style.setProperty('--form-label-color', branding.forms.label.color);
  root.style.setProperty('--form-label-margin-bottom', `${branding.forms.label.marginBottom}px`);
  
  root.style.setProperty('--form-input-height', branding.forms.input.height);
  root.style.setProperty('--form-input-font-size', branding.forms.input.fontSize);
  root.style.setProperty('--form-input-padding', branding.forms.input.padding);
  root.style.setProperty('--form-input-border-radius', branding.forms.input.borderRadius);
  root.style.setProperty('--form-input-border-color', branding.forms.input.borderColor);
  root.style.setProperty('--form-input-background', branding.forms.input.background);
  root.style.setProperty('--form-input-focus-border-color', branding.forms.input.focusBorderColor);
  root.style.setProperty('--form-input-placeholder-color', branding.forms.input.placeholderColor);
  root.style.setProperty('--form-input-focus-shadow-color', `${branding.primaryColor}1a`); // 10% opacity
  
  // Buttons
  root.style.setProperty('--form-button-height', `${branding.forms.button.height}px`);
  root.style.setProperty('--form-button-padding', branding.forms.button.padding);
  root.style.setProperty('--form-button-font-size', `${branding.forms.button.fontSize}px`);
  root.style.setProperty('--form-button-font-weight', branding.forms.button.fontWeight.toString());
  root.style.setProperty('--form-button-border-radius', `${branding.forms.button.borderRadius}px`);
  root.style.setProperty('--form-button-disabled-opacity', branding.forms.input.disabledOpacity.toString());
  
  root.style.setProperty('--form-button-primary-background', branding.forms.button.primary.background);
  root.style.setProperty('--form-button-primary-color', branding.forms.button.primary.color);
  root.style.setProperty('--form-button-primary-hover-background', branding.forms.button.primary.hoverBackground);
  
  root.style.setProperty('--form-button-secondary-background', branding.forms.button.secondary.background);
  root.style.setProperty('--form-button-secondary-color', branding.forms.button.secondary.color);
  root.style.setProperty('--form-button-secondary-border', branding.forms.button.secondary.border);
  root.style.setProperty('--form-button-secondary-hover-background', branding.forms.button.secondary.hoverBackground);
  
  // Text Colors
  root.style.setProperty('--text-color', branding.textColor);
};

const API_BASE_URL = getApiBaseUrl();
const PERSONAL_REGISTER_STEP_COUNT = 6;
const PERSONAL_REGISTRATION_PAYMENT_CONTEXT_KEY = 'personalRegistrationPaymentContext';

const PersonalRegister: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const stepperRef = useRef<HTMLDivElement>(null);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  
  // KYC state
  const [kycStatus, setKycStatus] = useState<'pending' | 'completed' | 'failed' | null>(null);
  const [kycApplicantId, setKycApplicantId] = useState<string | null>(null);
  const [kycAccessToken, setKycAccessToken] = useState<string | null>(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED'>('PENDING');
  const [registrationPaymentMethod, setRegistrationPaymentMethod] = useState<'STRIPE' | 'CRYPTO'>('STRIPE');
  const [registrationCryptoBlockchain, setRegistrationCryptoBlockchain] = useState('ethereum');
  const [registrationCryptoSymbol, setRegistrationCryptoSymbol] = useState('USDT');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = useState('');

  // Get form data from Redux and ensure all required properties exist
  const reduxData = useSelector((state: RootState) => state.registration.personal.data);
  
  const formData = {
    productId: reduxData?.productId || 0,
    productType: reduxData?.productType || '',
    registrationFee: reduxData?.registrationFee || 0,
    registrationFeeType: reduxData?.registrationFeeType || 'FIXED_ONE_OFF',
    requiresVerification: reduxData?.requiresVerification !== false,
    firstName: reduxData?.firstName || '',
    lastName: reduxData?.lastName || '',
    email: reduxData?.email || '',
    phoneNumber: reduxData?.phoneNumber || '',
    dateOfBirth: reduxData?.dateOfBirth || '',
    password: reduxData?.password || '',
    address: {
      street: reduxData?.address?.street || '',
      city: reduxData?.address?.city || '',
      state: reduxData?.address?.state || '',
      postalCode: reduxData?.address?.postalCode || '',
      country: reduxData?.address?.country || ''
    },
    employment: {
      employerName: reduxData?.employment?.employerName || '',
      jobTitle: reduxData?.employment?.jobTitle || '',
      monthlyIncome: reduxData?.employment?.monthlyIncome || 0,
      duration: reduxData?.employment?.duration || ''
    },
    terms: reduxData?.terms || false,
    marketing: reduxData?.marketing || false
  } as PersonalFormData;
  const selectedRegistrationFee = Number(reduxData?.registrationFee || 0);
  const selectedRegistrationFeeType = (reduxData?.registrationFeeType || 'FIXED_ONE_OFF') as 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
  const selectedRegistrationPaymentMethods = (reduxData?.registrationPaymentMethods || 'ALL') as 'ALL' | 'STRIPE' | 'CRYPTO';
  const requiresPayment = selectedRegistrationFeeType !== 'NO_FEE';
  const allowedPaymentMethods: Array<'STRIPE' | 'CRYPTO'> =
    selectedRegistrationPaymentMethods === 'STRIPE'
      ? ['STRIPE']
      : selectedRegistrationPaymentMethods === 'CRYPTO'
        ? ['CRYPTO']
        : ['STRIPE', 'CRYPTO'];
  const verificationRequired = formData.requiresVerification !== false;
  const steps = useMemo(
    () =>
      verificationRequired
        ? ['Product Selection', 'Personal', 'Address', 'Employment', 'Verify', 'KYC Verification']
        : ['Product Selection', 'Personal', 'Address', 'Employment', 'Verify', 'Payment & finish'],
    [verificationRequired]
  );

  useEffect(() => {
    initializeBrandingStyles();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      if (isMobileView) {
        scrollToCurrentStep();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    scrollToCurrentStep();
  }, [activeStep, isMobile]);

  useEffect(() => {
    setEmailVerified(false);
    setEmailVerificationSent(false);
    setEmailVerificationMessage('');
  }, [formData.email]);

  useEffect(() => {
    if (!allowedPaymentMethods.includes(registrationPaymentMethod)) {
      setRegistrationPaymentMethod(allowedPaymentMethods[0] || 'STRIPE');
    }
  }, [selectedRegistrationPaymentMethods]);

  const sendVerificationLink = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      setEmailVerificationMessage('');
      const result = await emailVerificationService.sendLink(formData.email);
      setEmailVerificationSent(true);
      setEmailVerificationMessage(result.message || 'Verification link sent. Check your inbox.');
    } catch (err: any) {
      setEmailVerificationMessage(
        err?.response?.data?.errors?.[0]
          || err?.response?.data?.message
          || 'Failed to send verification link.'
      );
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const refreshEmailVerificationStatus = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      const status = await emailVerificationService.getStatus(formData.email);
      setEmailVerified(Boolean(status.verified));
      setEmailVerificationMessage(status.verified
        ? 'Email verified. You can continue.'
        : 'Email not verified yet. Click the link sent to your inbox.'
      );
    } catch {
      setEmailVerificationMessage('Failed to check email verification status.');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment_result');
    const sessionId = params.get('session_id');
    if (!paymentResult) return;

    // Stripe may return in a new tab where Redux state is empty. Restore
    // the selected product/payment flags so we don't force the wrong flow.
    try {
      const raw = window.localStorage.getItem(PERSONAL_REGISTRATION_PAYMENT_CONTEXT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        dispatch(updatePersonalForm({
          productId: Number(saved?.productId || 0),
          productType: saved?.productType || '',
          registrationFee: Number(saved?.registrationFee || 0),
          registrationFeeType: saved?.registrationFeeType || 'FIXED_ONE_OFF',
          requiresVerification: saved?.requiresVerification !== false
        }));
      }
    } catch {
      // Ignore malformed persisted data and continue with runtime state.
    }

    const finalize = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveStep(PERSONAL_REGISTER_STEP_COUNT - 1);
    };

    if (paymentResult === 'cancelled') {
      setPaymentStatus('EXPIRED');
      setPaymentCompleted(false);
      setError('Payment was cancelled. You can try again.');
      finalize();
      return;
    }

    if (paymentResult === 'success' && sessionId) {
      const checkStatus = async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
          const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
          setPaymentSessionId(sessionId);
          setPaymentStatus(status);
          setPaymentCompleted(status === 'COMPLETED');
          if (status !== 'COMPLETED') {
            setError('');
            startPaymentStatusPolling(sessionId);
          }
        } catch {
          setError('Unable to verify payment status right now. Please try again.');
        } finally {
          finalize();
        }
      };
      checkStatus();
    }
  }, []);

  const scrollToCurrentStep = () => {
    if (isMobile && stepperRef.current && stepContainerRef.current) {
      const stepElements = stepContainerRef.current.querySelectorAll('.step');
      if (stepElements[activeStep]) {
        const stepElement = stepElements[activeStep] as HTMLElement;
        const stepperRect = stepperRef.current.getBoundingClientRect();
        const stepRect = stepElement.getBoundingClientRect();
        
        const scrollPosition = stepElement.offsetLeft - (stepperRect.width / 2) + (stepRect.width / 2);
        
        stepperRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleChange = (field: string, value: any) => {
    const fields = field.split('.');
    let updatedData: Partial<PersonalFormData>;
    
    if (fields.length === 1) {
      updatedData = { [field]: value };
    } else {
      const section = fields[0] as keyof PersonalFormData;
      updatedData = {
        [section]: {
          ...(formData[section] as any),
          [fields[1]]: value
        }
      };
    }
    
    dispatch(updatePersonalForm(updatedData));
  };

  const handleAddressChange = (field: string, value: string) => {
    dispatch(updatePersonalForm({
      address: {
        ...formData.address,
        [field]: value
      }
    }));
  };

  const fillWithMockData = () => {
    dispatch(updatePersonalForm({
      productId: 1,
      productType: 'PERSONAL',
      registrationFee: 25,
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@example.com',
      phoneNumber: '+1234567890',
      dateOfBirth: '1990-05-15',
      password: 'SecurePass123!',
      address: {
        street: '123 Main Street',
        city: 'New York',
        state: 'NY',
        postalCode: '10001',
        country: 'United States'
      },
      employment: {
        employerName: 'Tech Solutions Inc',
        jobTitle: 'Software Engineer',
        monthlyIncome: 7500,
        duration: '2 years'
      },
      terms: true,
      marketing: false
    }));
  };

  const clearAllData = () => {
    dispatch(clearPersonalForm());
    window.localStorage.removeItem(PERSONAL_REGISTRATION_PAYMENT_CONTEXT_KEY);
    setKycStatus(null);
    setKycApplicantId(null);
    setKycAccessToken(null);
    setShowKycModal(false);
    setPaymentCompleted(false);
    setPaymentStatus('PENDING');
    setPaymentSessionId('');
  };

  const submitPersonalRegistration = async () => {
    setLoading(true);
    setError('');
    try {
      // POST to Java API — body must match PersonalRegistrationRequestDto only (no registrationFee*, requiresVerification)
      const response = await axiosInstance.post('/auth/register/personal', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        password: formData.password,
        address: formData.address,
        employment: {
          employerName: formData.employment.employerName,
          jobTitle: formData.employment.jobTitle,
          monthlyIncome: String(formData.employment.monthlyIncome ?? ''),
          duration: formData.employment.duration
        },
        terms: formData.terms,
        marketing: formData.marketing,
        identifier: formData.phoneNumber,
        productId: formData.productId,
        productType: formData.productType
      });

      const body = response.data;
      const ok =
        response.status === 201 ||
        body?.status === 'success' ||
        body?.success === true;
      if (ok) {
        dispatch(clearPersonalForm());
        window.localStorage.removeItem(PERSONAL_REGISTRATION_PAYMENT_CONTEXT_KEY);
        navigate('/login', {
          state: {
            message: 'Registration successful! Please check your email for verification.',
            type: 'success'
          }
        });
      } else {
        setError(body?.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      const msg =
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : null) ||
        error.message ||
        'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    console.log('HandleNext called, activeStep:', activeStep, 'kycStatus:', kycStatus);
    if (activeStep === steps.length - 1) {
      if (requiresPayment && !paymentCompleted) {
        setError('Please complete the registration payment before continuing.');
        return;
      }
      if (!verificationRequired) {
        await submitPersonalRegistration();
        return;
      }
      if (kycStatus === 'completed') {
        console.log('KYC completed, submitting registration to backend');
        await submitPersonalRegistration();
      } else {
        console.log('Starting KYC process, current status:', kycStatus);
        await startKYCProcess();
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleRegistrationPayment = () => {
    setError('');
    if (!formData.productId || selectedRegistrationFee <= 0) {
      setError('Please select a tier with a valid registration fee before starting payment.');
      return;
    }
    if (registrationPaymentMethod === 'CRYPTO') {
      setError('Crypto payment for personal registration is not enabled yet. Please use Stripe for now.');
      return;
    }
    const startPayment = async () => {
      const response = await axios.post(`${API_BASE_URL}/api/public/registration-payments/create-session`, {
        registrationType: 'PERSONAL',
        email: formData.email,
        productId: formData.productId,
        currency: 'EUR',
        paymentMethod: registrationPaymentMethod,
        cryptoBlockchain: registrationCryptoBlockchain,
        cryptoSymbol: registrationCryptoSymbol
      });
      if (!response.data?.success || !response.data?.data?.checkoutUrl || !response.data?.data?.sessionId) {
        setError(response.data?.error || 'Failed to create payment session.');
        return;
      }
      const sessionId = response.data.data.sessionId as string;
      setPaymentSessionId(sessionId);
      setPaymentStatus('PROCESSING');
      window.localStorage.setItem(
        PERSONAL_REGISTRATION_PAYMENT_CONTEXT_KEY,
        JSON.stringify({
          productId: formData.productId,
          productType: formData.productType,
          registrationFee: formData.registrationFee,
          registrationFeeType: formData.registrationFeeType,
          requiresVerification: formData.requiresVerification
        })
      );
      window.open(response.data.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      startPaymentStatusPolling(sessionId);
    };
    startPayment().catch((err: any) => setError(err?.response?.data?.error || err?.message || 'Failed to start payment.'));
  };

  const startPaymentStatusPolling = (sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
        const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        setPaymentStatus(status);
        if (status === 'COMPLETED') {
          setPaymentCompleted(true);
          clearInterval(interval);
        }
        if (status === 'FAILED' || status === 'EXPIRED') {
          setPaymentCompleted(false);
          clearInterval(interval);
        }
      } catch {
        // keep polling
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 5000);
  };

  const startKYCProcess = async () => {
    try {
      setLoading(true);
      setError('');

      // Prepare KYC data
      const kycData: KYCApplicantData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address
      };

      // Use email as username for KYC
      const kycResponse = await kycService.createApplicant(kycData, formData.email);
      console.log('KYC Response:', kycResponse);

      if (kycResponse.success && kycResponse.data) {
        console.log('Setting KYC state:', {
          applicantId: kycResponse.data.applicantId,
          accessToken: kycResponse.data.accessToken ? '***' + kycResponse.data.accessToken.slice(-8) : 'null'
        });
        setKycApplicantId(kycResponse.data.applicantId);
        setKycAccessToken(kycResponse.data.accessToken);
        setShowKycModal(true);
      } else {
        setError(kycResponse.error || 'Failed to initialize KYC verification');
      }
    } catch (error: any) {
      console.error('KYC initialization error:', error);
      setError('Failed to initialize KYC verification');
    } finally {
      setLoading(false);
    }
  };

  const handleKYCComplete = (status: 'approved' | 'rejected' | 'pending') => {
    console.log('KYC Complete called with status:', status);
    setShowKycModal(false);
    setKycStatus(status === 'approved' ? 'completed' : 'failed');
    
    if (status === 'approved') {
      console.log('KYC approved, proceeding with registration');
      // Continue with registration
      handleNext();
    } else if (status === 'rejected') {
      console.log('KYC rejected');
      setError('KYC verification was rejected. Please try again or contact support.');
    } else {
      console.log('KYC pending review');
      setError('KYC verification is pending review. You will be notified once it\'s completed.');
    }
  };

  const handleKYCError = (error: string) => {
    setShowKycModal(false);
    setError(`KYC verification failed: ${error}`);
  };

  const handleKYCClose = async () => {
    console.log('KYC Close called, current status:', kycStatus, 'applicantId:', kycApplicantId);
    setShowKycModal(false);
    
    // If we have an applicantId, check the KYC status from backend
    if (kycApplicantId && !kycStatus) {
      console.log('Checking KYC status from backend for applicant:', kycApplicantId);
      try {
        setLoading(true);
        const response = await axios.get(`/kyc/status/${kycApplicantId}?username=${formData.email}`);
        if (response.data.success && response.data.data) {
          const status = response.data.data.status;
          if (status === 'approved') {
            setKycStatus('completed');
            // Proceed with registration
            handleNext();
            return;
          } else if (status === 'rejected') {
            setKycStatus('failed');
            setError('KYC verification was rejected. Please try again or contact support.');
            return;
          } else if (status === 'pending') {
            setKycStatus('failed');
            setError('KYC verification is pending review. You will be notified once it\'s completed.');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking KYC status:', error);
      } finally {
        setLoading(false);
      }
    }
    
    // If KYC was completed but user closed modal manually, 
    // we need to check the status and proceed accordingly
    if (kycStatus === 'completed') {
      // KYC was completed, proceed with registration
      handleNext();
    } else if (kycStatus === 'failed') {
      // KYC failed, show error
      setError('KYC verification failed. Please try again.');
    } else {
      // KYC was not completed, reset status
      setKycStatus(null);
      setKycApplicantId(null);
      setKycAccessToken(null);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.productId > 0 && formData.productType !== '';
      case 1:
        return formData.firstName && formData.lastName && formData.email && 
               formData.phoneNumber && formData.dateOfBirth && formData.password && emailVerified;
      case 2:
        return formData.address.street && formData.address.city && 
               formData.address.state && formData.address.postalCode && formData.address.country;
      case 3:
        return formData.employment.employerName && formData.employment.jobTitle && 
               formData.employment.monthlyIncome > 0 && formData.employment.duration;
      case 4:
        return formData.terms;
      case 5:
        if (!verificationRequired) {
          return !requiresPayment || paymentCompleted;
        }
        return kycStatus === 'completed' || kycStatus === null;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ProductSelection
            customerType="PERSONAL"
            onProductSelect={(productId, productType, registrationFee, registrationFeeType, requiresVerificationFlag, registrationPaymentMethods) => {
              const needsVerification = requiresVerificationFlag !== false;
              handleChange('productId', productId);
              handleChange('productType', productType);
              handleChange('registrationFee', Number(registrationFee || 0));
              handleChange('registrationFeeType', registrationFeeType || 'FIXED_ONE_OFF');
              handleChange('registrationPaymentMethods', registrationPaymentMethods || 'ALL');
              handleChange('requiresVerification', needsVerification);
              setKycStatus(null);
              setKycApplicantId(null);
              setKycAccessToken(null);
              setShowKycModal(false);
              setPaymentCompleted(registrationFeeType === 'NO_FEE');
              setPaymentStatus(registrationFeeType === 'NO_FEE' ? 'COMPLETED' : 'PENDING');
            }}
            selectedProductId={formData.productId}
          />
        );
      case 1:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="Enter your first name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Enter your last name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={formData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Enter your email address"
              />
              <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={sendVerificationLink}
                  disabled={emailVerificationLoading || !formData.email}
                  style={{
                    ...getInputStyle(),
                    width: 'auto',
                    height: 40,
                    padding: '0 14px',
                    cursor: emailVerificationLoading ? 'not-allowed' : 'pointer',
                    background: branding.forms.button.secondary.background
                  }}
                >
                  {emailVerificationLoading ? 'Sending...' : 'Send verification link'}
                </button>
                {emailVerificationSent && (
                  <button
                    type="button"
                    onClick={refreshEmailVerificationStatus}
                    disabled={emailVerificationLoading}
                    style={{
                      ...getInputStyle(),
                      width: 'auto',
                      height: 40,
                      padding: '0 14px',
                      cursor: emailVerificationLoading ? 'not-allowed' : 'pointer',
                      background: '#f8fafc'
                    }}
                  >
                    I verified, check status
                  </button>
                )}
              </div>
              {(emailVerificationMessage || emailVerified) && (
                <p style={{ marginTop: 8, marginBottom: 0, color: emailVerified ? '#16a34a' : '#475569', fontSize: 13 }}>
                  {emailVerified ? 'Email verified. You can continue.' : emailVerificationMessage}
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input
                type="date"
                className="form-input"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Create a password"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group full-width">
              <h3>Address Information</h3>
            </div>
            <div className="form-group full-width">
              <label className="form-label">Street Address</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="Enter your street address"
              />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder="Enter your city"
              />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                placeholder="Enter your state"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.postalCode}
                onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                placeholder="Enter your postal code"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                type="text"
                className="form-input"
                value={formData.address.country}
                onChange={(e) => handleAddressChange('country', e.target.value)}
                placeholder="Enter your country"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="form-group full-width">
              <h3>Employment Information</h3>
            </div>
            <div className="form-group full-width">
              <label className="form-label">Employer Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.employment.employerName}
                onChange={(e) => handleChange('employment.employerName', e.target.value)}
                placeholder="Enter your employer's name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.employment.jobTitle}
                onChange={(e) => handleChange('employment.jobTitle', e.target.value)}
                placeholder="Enter your job title"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Monthly Income</label>
              <input
                type="number"
                className="form-input"
                value={formData.employment.monthlyIncome || ''}
                onChange={(e) => handleChange('employment.monthlyIncome', Number(e.target.value))}
                placeholder="Enter your monthly income"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Employment Duration</label>
              <input
                type="text"
                className="form-input"
                value={formData.employment.duration}
                onChange={(e) => handleChange('employment.duration', e.target.value)}
                placeholder="How long have you been employed here?"
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ 
              padding: '24px',
              background: branding.forms.input.background,
              borderRadius: branding.forms.input.borderRadius,
              border: `1px solid ${branding.forms.input.borderColor}`
            }}>
              <h3 style={{
                fontSize: branding.forms.label.fontSize,
                fontWeight: branding.forms.label.fontWeight,
                color: branding.forms.label.color,
                marginBottom: '16px'
              }}>
                Review Your Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <strong style={{ color: branding.forms.label.color }}>Name:</strong>
                  <span style={{ marginLeft: '8px' }}>{formData.firstName} {formData.lastName}</span>
                </div>
                <div>
                  <strong style={{ color: branding.forms.label.color }}>Email:</strong>
                  <span style={{ marginLeft: '8px' }}>{formData.email}</span>
                </div>
                <div>
                  <strong style={{ color: branding.forms.label.color }}>Phone:</strong>
                  <span style={{ marginLeft: '8px' }}>{formData.phoneNumber}</span>
                </div>
                <div>
                  <strong style={{ color: branding.forms.label.color }}>Date of Birth:</strong>
                  <span style={{ marginLeft: '8px' }}>{formData.dateOfBirth}</span>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong style={{ color: branding.forms.label.color }}>Address:</strong>
                  <span style={{ marginLeft: '8px' }}>
                    {formData.address.street}, {formData.address.city}, {formData.address.state} {formData.address.postalCode}, {formData.address.country}
                  </span>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong style={{ color: branding.forms.label.color }}>Employment:</strong>
                  <span style={{ marginLeft: '8px' }}>
                    {formData.employment.jobTitle} at {formData.employment.employerName}
                  </span>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '4px' }}
                  checked={formData.terms}
                  onChange={(e) => handleChange('terms', e.target.checked)}
                />
                <label style={{ ...getLabelStyle(), marginBottom: 0 }}>
                  I agree to the Terms and Conditions and Privacy Policy
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '4px' }}
                  checked={formData.marketing}
                  onChange={(e) => handleChange('marketing', e.target.checked)}
                />
                <label style={{ ...getLabelStyle(), marginBottom: 0 }}>
                  I agree to receive marketing communications from {branding.sidebar.logoText}
                </label>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {verificationRequired && (
            <div style={{ 
              padding: '24px',
              background: branding.forms.input.background,
              borderRadius: branding.forms.input.borderRadius,
              border: `1px solid ${branding.forms.input.borderColor}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🆔</div>
              <h3 style={{
                fontSize: branding.forms.label.fontSize,
                fontWeight: branding.forms.label.fontWeight,
                color: branding.forms.label.color,
                marginBottom: '16px'
              }}>
                Identity Verification Required
              </h3>
              <p style={{ 
                color: '#666', 
                fontSize: 16, 
                lineHeight: 1.5,
                marginBottom: '24px'
              }}>
                To complete your registration, we need to verify your identity. 
                This is a secure process that helps protect your account and comply with regulations.
              </p>
              
              {kycStatus === 'completed' && (
                <div style={{
                  padding: '16px',
                  background: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: 8,
                  color: '#155724',
                  marginBottom: '16px'
                }}>
                  ✅ Identity verification completed successfully!
                </div>
              )}
              
              {kycStatus === 'failed' && (
                <div style={{
                  padding: '16px',
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: 8,
                  color: '#721c24',
                  marginBottom: '16px'
                }}>
                  ❌ Identity verification failed. Please try again.
                </div>
              )}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: 20 }}>📱</span>
                  <span>Photo ID verification</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: 20 }}>📸</span>
                  <span>Selfie verification</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: 20 }}>🔒</span>
                  <span>Secure and encrypted</span>
                </div>
              </div>
            </div>
            )}

            {!verificationRequired && (
              <div style={{
                padding: '20px',
                background: '#f0fdf4',
                borderRadius: branding.forms.input.borderRadius,
                border: '1px solid #bbf7d0',
                color: '#166534'
              }}>
                <strong>No identity verification required</strong> for your selected product. Complete any registration payment below, then submit your application.
              </div>
            )}

            <div style={{
              padding: '20px',
              background: '#f8fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: 8 }}>Registration Payment</h4>
              <p style={{ marginTop: 0, color: '#475569' }}>
                {selectedRegistrationFeeType === 'NO_FEE' && 'No registration payment is required for your selected tier.'}
                {selectedRegistrationFeeType === 'FIXED_ONE_OFF' && <>A one-time registration fee of <strong>EUR {selectedRegistrationFee.toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                {selectedRegistrationFeeType === 'RECURRING_MONTHLY' && <>A recurring monthly fee of <strong>EUR {selectedRegistrationFee.toFixed(2)}</strong> for your selected tier is required before account creation.</>}
              </p>
              {requiresPayment && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-input"
                      value={registrationPaymentMethod}
                      onChange={(e) => setRegistrationPaymentMethod(e.target.value as 'STRIPE' | 'CRYPTO')}
                    >
                      {allowedPaymentMethods.includes('STRIPE') && <option value="STRIPE">Card (Stripe)</option>}
                      {allowedPaymentMethods.includes('CRYPTO') && <option value="CRYPTO">Crypto</option>}
                    </select>
                  </div>
                  {registrationPaymentMethod === 'CRYPTO' && (
                    <>
                      <div>
                        <label className="form-label">Crypto Network</label>
                        <select
                          className="form-input"
                          value={registrationCryptoBlockchain}
                          onChange={(e) => setRegistrationCryptoBlockchain(e.target.value)}
                        >
                          <option value="ethereum">Ethereum</option>
                          <option value="polygon">Polygon</option>
                          <option value="bitcoin">Bitcoin</option>
                          <option value="stellar">Stellar</option>
                          <option value="solana">Solana</option>
                          <option value="cardano">Cardano</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Symbol</label>
                        <input
                          type="text"
                          className="form-input"
                          value={registrationCryptoSymbol}
                          onChange={(e) => setRegistrationCryptoSymbol(e.target.value.toUpperCase())}
                          placeholder="USDT"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {requiresPayment && (
                  <button
                    type="button"
                    onClick={handleRegistrationPayment}
                    style={{
                      height: branding.forms.button.height,
                      padding: branding.forms.button.padding,
                      fontSize: branding.forms.button.fontSize,
                      fontWeight: branding.forms.button.fontWeight,
                      borderRadius: branding.forms.button.borderRadius,
                      background: branding.forms.button.primary.background,
                      color: branding.forms.button.primary.color,
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      {registrationPaymentMethod === 'STRIPE' ? <FaCcStripe /> : '₿'}
                      {registrationPaymentMethod === 'STRIPE'
                        ? (selectedRegistrationFeeType === 'RECURRING_MONTHLY' ? 'Start Monthly Subscription' : 'Pay Registration Fee')
                        : 'Pay with Crypto'}
                    </span>
                  </button>
                )}
                <span style={{ color: paymentCompleted ? '#16a34a' : '#475569' }}>
                  Status: <strong>{paymentStatus}</strong>
                </span>
              </div>
              {paymentSessionId && <p style={{ marginBottom: 0, fontSize: 12, color: '#64748b' }}>Session: {paymentSessionId}</p>}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getInputStyle = () => ({
    height: branding.forms.input.height,
    fontSize: branding.forms.input.fontSize,
    padding: branding.forms.input.padding,
    borderRadius: branding.forms.input.borderRadius,
    border: `1px solid ${branding.forms.input.borderColor}`,
    background: branding.forms.input.background,
    width: '100%',
    color: branding.textColor,
  });

  const getLabelStyle = () => ({
    fontSize: branding.forms.label.fontSize,
    fontWeight: branding.forms.label.fontWeight,
    color: branding.forms.label.color,
    marginBottom: branding.forms.label.marginBottom,
    display: 'block'
  });

  return (
    <div className="business-register-container">
      {/* Sidebar */}
      <div className="business-register-sidebar">
        <div>
          <div style={{
            fontSize: branding.sidebar.logo.fontSize,
            fontWeight: branding.sidebar.logo.fontWeight,
            marginBottom: branding.sidebar.logo.marginBottom,
            color: branding.sidebar.logo.color
          }}>
            {branding.sidebar.logoText}
          </div>
          <h1 style={{
            fontSize: branding.sidebar.title.fontSize,
            fontWeight: branding.sidebar.title.fontWeight,
            marginBottom: branding.sidebar.title.marginBottom,
            color: branding.sidebar.title.color
          }}>
            Start Your Online Journey Today
          </h1>
          <p style={{
            fontSize: branding.sidebar.text.fontSize,
            lineHeight: branding.sidebar.text.lineHeight,
            color: branding.sidebar.text.color
          }}>
            Join thousands of satisfied customers who trust us for a secure and reliable online experience.
            Enjoy a seamless digital journey with advanced security and personalized support.
          </p>
        </div>
        <div style={{
          fontSize: branding.sidebar.footer.fontSize,
          color: branding.sidebar.footer.color
        }}>
          © {new Date().getFullYear()} {branding.sidebar.logoText}. All rights reserved.
        </div>
      </div>

      {/* Main Content */}
      <div className="business-register-main">
        {/* Stepper */}
        <div className="stepper" ref={stepperRef}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 'fit-content',
            position: 'relative',
            padding: '0 16px'
          }}>
            {steps.map((label, index) => (
              <div key={label} className={`step ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}>
                <div className="step-number">
                  {index < activeStep ? '✓' : index + 1}
                </div>
                <div className="step-label">{label}</div>
                {index < steps.length - 1 && <div className="step-line" />}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="form-container" ref={stepContainerRef}>
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '8px'
            }}>
              {activeStep === 0 && "Select a Product"}
              {activeStep === 1 && "Personal Information"}
              {activeStep === 2 && "Address Details"}
              {activeStep === 3 && "Employment Information"}
              {activeStep === 4 && "Verify Your Account"}
              {activeStep === 5 && (verificationRequired ? 'KYC Verification' : 'Payment & finish')}
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0
            }}>
              {activeStep === 0 && "Choose the banking product that best suits your needs"}
              {activeStep === 1 && "Please provide your basic personal information"}
              {activeStep === 2 && "Enter your current residential address"}
              {activeStep === 3 && "Tell us about your employment"}
              {activeStep === 4 && "Review and confirm your information"}
              {activeStep === 5 && (verificationRequired ? 'Complete identity verification to secure your account' : 'Pay any required fee, then complete registration')}
            </p>
          </div>

          {/* Mock Data Buttons for Testing */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginBottom: '20px'
          }}>
            {/* TEMPORARILY HIDDEN - Fill Mock Data button 
            <button
              type="button"
              onClick={fillWithMockData}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: '#3b82f6',
                border: '1px solid #3b82f6',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#3b82f6';
              }}
            >
              Fill Mock Data
            </button>
            */}
            <button
              type="button"
              onClick={clearAllData}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: '#ef4444',
                border: '1px solid #ef4444',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#ef4444';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#ef4444';
              }}
            >
              Clear All Data
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={(e) => e.preventDefault()}>
            {renderStepContent()}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '16px',
              marginTop: '40px'
            }}>
              {activeStep > 0 && (
                <button
                  type="button"
                  style={{
                    height: branding.forms.button.height,
                    padding: branding.forms.button.padding,
                    fontSize: branding.forms.button.fontSize,
                    fontWeight: branding.forms.button.fontWeight,
                    borderRadius: branding.forms.button.borderRadius,
                    background: branding.forms.button.secondary.background,
                    color: branding.forms.button.secondary.color,
                    border: `1px solid ${branding.forms.button.secondary.border}`,
                    cursor: 'pointer',
                    opacity: loading ? branding.forms.input.disabledOpacity : 1
                  }}
                  onClick={handleBack}
                  disabled={loading}
                >
                  Back
                </button>
              )}
              <button
                type="button"
                style={{
                  height: branding.forms.button.height,
                  padding: branding.forms.button.padding,
                  fontSize: branding.forms.button.fontSize,
                  fontWeight: branding.forms.button.fontWeight,
                  borderRadius: branding.forms.button.borderRadius,
                  background: branding.forms.button.primary.background,
                  color: branding.forms.button.primary.color,
                  border: 'none',
                  cursor: 'pointer',
                  opacity: (loading || !isStepValid()) ? branding.forms.input.disabledOpacity : 1
                }}
                onClick={handleNext}
                disabled={loading || !isStepValid()}
              >
                {loading ? 'Loading...' : 
                 activeStep === steps.length - 1 ? 
                   (!verificationRequired
                     ? ((!requiresPayment || paymentCompleted) ? 'Complete Registration' : 'Pay fee to continue')
                     : (kycStatus === 'completed' ? 'Complete Registration' : 'Start Verification')) : 
                   'Continue'}
              </button>
            </div>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: '32px',
            color: branding.forms.label.color,
            fontSize: branding.forms.input.fontSize
          }}>
            Already have an account?
            <Link to="/login" style={{
              color: branding.primaryColor,
              textDecoration: 'none',
              fontWeight: branding.forms.button.fontWeight,
              marginLeft: '4px'
            }}>
              Sign in here
            </Link>
          </div>
        </div>
      </div>

      {showKycModal && kycApplicantId && (
        <SumsubKYC
          applicantId={kycApplicantId}
          accessToken={kycAccessToken || ''}
          onComplete={handleKYCComplete}
          onError={handleKYCError}
          onClose={handleKYCClose}
        />
      )}
    </div>
  );
};

export default PersonalRegister; 